import { useState } from "react";
import Header from "@/components/Header";
import { useParams, useNavigate } from "react-router-dom";
import { useTrailById, useTrailSegments } from "@/hooks/useTrails";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Mountain, 
  MapPin, 
  Ruler, 
  AlertTriangle,
  ArrowLeft,
  Share2,
  Route,
  Sparkles,
  Loader2,
  Lock,
  Clock,
  Home,
  Youtube,
  Play
} from "lucide-react";
import YouTubePlayerDialog from "@/components/YouTubePlayerDialog";
import TrailMap from "@/components/TrailMap";
import { useTrailRoutes, useTrailRuns } from "@/hooks/useRelatedData";
import { formatDistanceToNow } from "date-fns";
import { useTrailDescription } from "@/hooks/useTrailDescription";
import LockedContent from "@/components/LockedContent";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import TrailDescriptionFeedback from "@/components/TrailDescriptionFeedback";
import { TrailPreferenceButtons } from "@/components/TrailPreferenceButtons";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

const TrailDetail = () => {
  const { trailId } = useParams();
  const navigate = useNavigate();
  const { trail, segments, isLoading, error } = useTrailById(trailId);
  const { segments: allSegments } = useTrailSegments();
  const { routes, isLoading: routesLoading } = useTrailRoutes(trailId);
  const { runs, isLoading: runsLoading } = useTrailRuns(trailId);
  const { description, isLoading: isLoadingDescription, isGenerating, generateDescription } = useTrailDescription(trailId);
  const { canAccess } = useSubscription();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [videoDialog, setVideoDialog] = useState<{ url: string; title: string } | null>(null);

  const handleShare = async () => {
    const url = window.location.href;
    const title = `Check out ${trail?.name} on Airing Down!`;
    
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !trail) {
    const redirectPath = user ? '/explore' : '/';
    const buttonText = user ? 'Back to Explorer' : 'Return to Home';
    const ButtonIcon = user ? ArrowLeft : Home;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Mountain className="h-16 w-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="text-2xl font-bold text-foreground mb-4">Trail Not Found</h1>
            <p className="text-muted-foreground mb-6">
              {error || "The requested trail could not be found."}
            </p>
            <Button onClick={() => navigate(redirectPath)}>
              <ButtonIcon className="h-4 w-4 mr-2" />
              {buttonText}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const totalDistance = segments.reduce((sum, segment) => sum + (segment.length_miles || 0), 0);
  const avgDifficulty = segments.length > 0 ? 
    segments.filter(s => s.difficulty).length > 0 ? 'moderate' : 'unknown' : 'unknown';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Map Section with Minimal Overlay */}
      <div className="relative w-full h-[400px] overflow-hidden">
        <TrailMap 
          className="w-full h-full" 
          segments={allSegments}
          singleTrailId={trailId}
          disableScrollZoom={true}
        />
        
        {/* Minimal Floating Overlay */}
        <div className="absolute top-4 left-4 z-10">
          <Card className="bg-background/90 backdrop-blur-sm border-border shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/explore')}
                  className="h-8 w-8 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-xl font-bold text-foreground">{trail.name}</h1>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground ml-11">
                <MapPin className="h-4 w-4" />
                <span>{segments[0]?.forest_name || segments[0]?.district_name || 'Southern California'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Horizontal Stats Bar */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 items-center">
            <div className="flex items-center gap-3">
              <Ruler className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Distance</p>
                <p className="text-lg font-semibold">{totalDistance.toFixed(1)} mi</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mountain className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground">Difficulty</p>
                <Badge variant={avgDifficulty === 'moderate' ? 'secondary' : 'outline'} className="text-xs">
                  {avgDifficulty}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Route className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground">Segments</p>
                <p className="text-lg font-semibold">{segments.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Route className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground">Routes</p>
                <p className="text-lg font-semibold">{routes.length}</p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              {isMobile ? (
                <Button variant="outline" size="icon" onClick={handleShare}>
                  <Share2 className="h-4 w-4" />
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="w-full" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
            </div>
            <div className="flex items-center justify-center col-span-2 sm:col-span-1 lg:col-span-2">
              <TrailPreferenceButtons 
                trailId={trailId!} 
                variant={isMobile ? "compact" : "default"}
                className={isMobile ? "" : "w-full"} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections Below Map */}
      <main className="container mx-auto px-4 py-8 relative z-10 bg-background">
        {/* Trail Description Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Trail Description</span>
              {description && (
                <Badge variant={description.source === "ai_generated" ? "secondary" : "default"}>
                  {description.source === "ai_generated" ? "AI Generated" : description.source === "official" ? "Official" : "User Submitted"}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingDescription ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
                <div className="h-4 bg-muted rounded w-4/6"></div>
              </div>
            ) : description ? (
              <div className="space-y-4">
                <p className="text-foreground leading-relaxed whitespace-pre-line">
                  {description.description}
                </p>
                {description.resource_links?.youtube && description.resource_links.youtube.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                      <Youtube className="h-4 w-4" />
                      Trail Videos
                    </h4>
                    <div className="space-y-2">
                      {description.resource_links.youtube.map((link: { url: string; title: string }, i: number) => (
                        <button
                          key={i}
                          onClick={() => setVideoDialog({ url: link.url, title: link.title })}
                          className="flex items-center gap-2 text-sm text-primary hover:underline w-full text-left"
                        >
                          <Play className="h-4 w-4 shrink-0" />
                          <span className="truncate">{link.title || link.url}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {videoDialog && (
                  <YouTubePlayerDialog
                    open={!!videoDialog}
                    onOpenChange={(open) => !open && setVideoDialog(null)}
                    url={videoDialog.url}
                    title={videoDialog.title}
                  />
                )}
                <div className="flex items-center gap-4 pt-2 border-t">
                  <TrailDescriptionFeedback
                    trailId={trailId!}
                    descriptionId={description.id}
                  />
                  <span className="text-xs text-muted-foreground">
                    Was this description helpful?
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 space-y-4">
                <p className="text-muted-foreground">No trail info available yet.</p>
                <Button 
                  onClick={generateDescription}
                  disabled={isGenerating}
                  className="gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Researching...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Research Trail
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  AI will research this trail and compile relevant information
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trail Information Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Trail Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {segments[0]?.forest_name && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Forest</span>
                  <span className="text-sm font-medium">{segments[0].forest_name}</span>
                </div>
              )}
              {segments[0]?.district_name && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">District</span>
                  <span className="text-sm font-medium">{segments[0].district_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Source</span>
                <span className="text-sm font-medium">USFS MVUM</span>
              </div>
            </CardContent>
          </Card>

          {segments.some(s => s.allowed_vehicles?.length) && (
            <Card>
              <CardHeader>
                <CardTitle>Allowed Vehicles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Array.from(new Set(segments.flatMap(s => s.allowed_vehicles || []))).map((vehicle) => (
                    <Badge key={vehicle} variant="outline">
                      {vehicle.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Recent Runs</span>
                <span className="text-sm font-semibold">{runs.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">User Routes</span>
                <span className="text-sm font-semibold">{routes.length}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Segments Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Trail Segments ({segments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {segments.map((segment, index) => (
                <div key={segment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium">
                      {segment.trail_name || `Segment ${segment.segment_order || index + 1}`}
                    </h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span>{(segment.length_miles || 0).toFixed(2)} mi</span>
                      {segment.difficulty && (
                        <Badge variant="outline" className="text-xs">
                          {segment.difficulty}
                        </Badge>
                      )}
                      {segment.surface_type && (
                        <span>{segment.surface_type}</span>
                      )}
                      {segment.maintenance_level && (
                        <span className="text-xs">{segment.maintenance_level}</span>
                      )}
                    </div>
                  </div>
                  
                  {segment.features && segment.features.length > 0 && (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      <span className="text-xs text-muted-foreground">
                        {segment.features.length} features
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Routes Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>User Routes ({routes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {routesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : routes.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No user-created routes using this trail yet.
              </p>
            ) : (
              <div className="space-y-4">
                {routes.map((route) => (
                  <div 
                    key={route.id} 
                    className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate(`/route/${route.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{route.name}</h4>
                        {route.description && (
                          <p className="text-sm text-muted-foreground mt-1">{route.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          {route.length_miles && (
                            <span className="flex items-center gap-1">
                              <Ruler className="h-3 w-3" />
                              {route.length_miles.toFixed(1)} mi
                            </span>
                          )}
                          {route.difficulty && (
                            <Badge variant="outline" className="text-xs">
                              {route.difficulty}
                            </Badge>
                          )}
                          <span>
                            Created {formatDistanceToNow(new Date(route.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        View Route
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Activity ({runs.length})</span>
              {runs.length > 0 && !canAccess('adventurer') && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Adventurer
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {runsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : runs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No recent activity on this trail.
              </p>
            ) : canAccess('adventurer') ? (
              <div className="space-y-4">
                {runs.slice(0, 10).map((run) => (
                  <div 
                    key={run.id} 
                    className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate(`/runs/${run.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{run.equipment_type}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(run.end_time || run.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          {run.distance_miles && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Ruler className="h-3 w-3" />
                              {run.distance_miles.toFixed(1)} mi
                            </span>
                          )}
                          {run.duration_minutes && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {run.duration_minutes} min
                            </span>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        View Run
                      </Button>
                    </div>
                  </div>
                ))}
                {runs.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Showing 10 of {runs.length} runs
                  </p>
                )}
              </div>
            ) : (
              <LockedContent
                requiredTier="adventurer"
                featureDescription="view community run details"
                blurContent={true}
              >
                <div className="space-y-4">
                  {runs.slice(0, 3).map((run) => (
                    <div 
                      key={run.id} 
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{run.equipment_type}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(run.end_time || run.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            {run.distance_miles && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Ruler className="h-3 w-3" />
                                {run.distance_miles.toFixed(1)} mi
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </LockedContent>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TrailDetail;
